import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, GoldButton } from "@/components/admin/AdminLayout";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const FIELDS = [
  { k: "company_name", l: "Company name" },
  { k: "company_nif", l: "Company NIF" },
  { k: "company_address", l: "Company address", full: true },
  { k: "admin_email", l: "Admin email", type: "email" },
  { k: "support_phone", l: "Support phone" },
  { k: "support_whatsapp", l: "Support WhatsApp" },
];

export default function SystemSettings() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/settings/system").then((r) => setCfg(r.data)); }, []);
  if (!cfg) return <div className="p-10 text-slate-500">Loading…</div>;

  const save = async () => {
    setSaving(true);
    try { await api.put("/admin/settings/system", cfg); toast.success("System settings saved"); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="p-10 max-w-[1200px]">
      <PageHeader eyebrow="Settings · Global" title="System settings" description="Company info, defaults, and platform-wide operational rules.">
        <GoldButton onClick={save} disabled={saving} data-testid="save-system">{saving ? "Saving…" : "Save"}</GoldButton>
      </PageHeader>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="border border-slate-200 bg-white p-6">
          <p className="label-caps mb-5">Company</p>
          <div className="grid grid-cols-2 gap-4">
            {FIELDS.map((f) => (
              <div key={f.k} className={f.full ? "col-span-2" : ""}>
                <label className="label-caps block mb-2">{f.l}</label>
                <Input type={f.type || "text"} value={cfg[f.k] || ""} onChange={(e) => setCfg({ ...cfg, [f.k]: e.target.value })} className="input-dt" data-testid={`sys-${f.k}`} />
              </div>
            ))}
          </div>
        </div>

        <div className="border border-slate-200 bg-white p-6">
          <p className="label-caps mb-5">Localisation & operations</p>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label-caps block mb-2">Default language</label>
              <select value={cfg.default_language} onChange={(e) => setCfg({ ...cfg, default_language: e.target.value })} className="input-dt" data-testid="sys-lang">
                <option value="PT">Português</option>
                <option value="EN">English</option>
                <option value="ES">Español</option>
                <option value="FR">Français</option>
              </select>
            </div>
            <div>
              <label className="label-caps block mb-2">Currency</label>
              <select value={cfg.currency} onChange={(e) => setCfg({ ...cfg, currency: e.target.value })} className="input-dt">
                <option value="EUR">EUR (€)</option>
                <option value="USD">USD ($)</option>
                <option value="GBP">GBP (£)</option>
              </select>
            </div>
            <div className="col-span-2">
              <label className="label-caps block mb-2">Timezone</label>
              <Input value={cfg.timezone || ""} onChange={(e) => setCfg({ ...cfg, timezone: e.target.value })} className="input-dt" />
            </div>
            <div>
              <label className="label-caps block mb-2">Min advance booking (h)</label>
              <Input type="number" value={cfg.booking_advance_min_hours || 2} onChange={(e) => setCfg({ ...cfg, booking_advance_min_hours: Number(e.target.value) })} className="input-dt" />
            </div>
            <div>
              <label className="label-caps block mb-2">Cancellation window (h)</label>
              <Input type="number" value={cfg.cancellation_window_hours || 24} onChange={(e) => setCfg({ ...cfg, cancellation_window_hours: Number(e.target.value) })} className="input-dt" />
            </div>
            <div className="col-span-2">
              <label className="label-caps block mb-2">Document alert days</label>
              <Input
                value={(cfg.document_alert_days || []).join(", ")}
                onChange={(e) => setCfg({ ...cfg, document_alert_days: e.target.value.split(",").map(x => Number(x.trim())).filter(Boolean) })}
                className="input-dt"
                data-testid="sys-alert-days"
                placeholder="30, 15, 7"
              />
            </div>
            <div className="col-span-2 pt-4 border-t border-slate-200">
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={cfg.auto_assign_enabled} onChange={(e) => setCfg({ ...cfg, auto_assign_enabled: e.target.checked })} className="accent-[#f59e0b] w-4 h-4" data-testid="sys-autoassign" />
                <div>
                  <div className="text-sm text-[#0a0f1e]">Auto-assignment engine</div>
                  <div className="text-xs text-slate-500">Automatically assign drivers to paid bookings</div>
                </div>
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
